import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import { isDebugEnabled } from '../../functions/debugging';
import { useCreation } from '../../functions/hooks/useCreation';
import { makeDebug } from '../../utils/makeDebug';

const debug = makeDebug('media-playback', isDebugEnabled('mediaPlayback'));

type PlaybackInfo = {
  positionMillis: number;
  refCount: number;
};

export type MediaPlaybackContextProps = {
  playbacks$: BehaviorSubject<Record<string, PlaybackInfo>>;
};

export const MediaPlaybackContext = createContext<MediaPlaybackContextProps>(
  null as any,
);

export const MediaPlaybackProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const playbacks$ = useCreation(
      () => new BehaviorSubject<Record<string, PlaybackInfo>>({}),
      [],
    );
    return (
      <MediaPlaybackContext.Provider value={{ playbacks$ }}>
        {children}
      </MediaPlaybackContext.Provider>
    );
  },
);

export const useMediaPlaybackContext = () => useContext(MediaPlaybackContext);

export const useContinuePlaybackControl = (id: string) => {
  const { playbacks$ } = useMediaPlaybackContext();

  const getPositionMillis = useCallback(() => {
    return playbacks$.value[id].positionMillis ?? 0;
  }, [id, playbacks$]);

  const readyForUpdateTime = useRef(false);

  useEffect(() => {
    if (!playbacks$.value[id]) {
      playbacks$.value[id] = { positionMillis: 0, refCount: 0 };
    }
    playbacks$.value[id].refCount++;
    debug(
      'playback %s ref count increased to: %d',
      id,
      playbacks$.value[id].refCount,
    );

    return () => {
      playbacks$.value[id].refCount--;
      debug(
        'playback %s ref count decreased to: %d',
        id,
        playbacks$.value[id].refCount,
      );
      if (playbacks$.value[id].refCount <= 0) {
        playbacks$.value[id].positionMillis = 0;
        playbacks$.value[id].refCount = 0;
        debug('playback %s reset', id);
      }
    };
  }, [id, playbacks$]);

  const updatePositionMillis = useCallback(
    (positionMillis: number) => {
      if (!readyForUpdateTime.current) return;
      if (!playbacks$.value[id]) {
        playbacks$.value[id] = { positionMillis: 0, refCount: 0 };
      }
      playbacks$.value[id].positionMillis = positionMillis;
    },
    [id, playbacks$],
  );

  const onResume = useCallback(() => {
    readyForUpdateTime.current = true;
  }, []);

  const onPause = useCallback(() => {
    readyForUpdateTime.current = false;
  }, []);

  return {
    getPositionMillis,
    updatePositionMillis,
    onResume,
    onPause,
  };
};
